import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import { getApiErrorMessage } from '../utils/apiError';
import { planDeleteFailure, planEventRemoval } from './performanceDelete';
import { MAX_ACTIVE_EVENTS, activeEventCount, canAddActiveEvent } from './performanceCap';
import './PerformanceSettings.css';

const DEFAULT_MAX_EVENTS = MAX_ACTIVE_EVENTS;

const emptyDraft = () => ({
  name: '',
  kind: 'timed',
  split_count: '',
  attempt_count: '10',
  effectiveness_threshold: '',
  target_split: '',
});

const parseDurationToMs = (raw) => {
  const value = (raw || '').trim().replace(',', '.');
  if (!value) return null;

  const durationMatch = value.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
  if (durationMatch) {
    const hours = Number(durationMatch[1]);
    const minutes = Number(durationMatch[2]);
    const seconds = Number(durationMatch[3]);
    if (minutes > 59 || seconds > 59) return null;
    const ms = hours * 3600000 + minutes * 60000 + seconds * 1000;
    return ms > 0 ? ms : null;
  }

  // Compatibilidad con el formato anterior MM:SS.cc.
  const match = value.match(/^(?:(\d+):)?(\d+)(?:\.(\d{1,3}))?$/);
  if (!match) return null;
  const minutes = Number(match[1] || 0);
  const seconds = Number(match[2]);
  let fraction = match[3] || '0';
  while (fraction.length < 3) fraction += '0';
  const ms = minutes * 60000 + seconds * 1000 + Number(fraction.slice(0, 3));
  return ms > 0 ? ms : null;
};

const formatDurationInput = (ms) => {
  if (!ms || ms < 1) return '';
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const toPayloadEvent = (event) => {
  const kind = event.kind === 'score' ? 'score' : 'timed';
  const threshold = Number(event.effectiveness_threshold);
  const targetMs = Number(event.target_split_ms) || parseDurationToMs(event.target_split);
  return {
    id: event.id || undefined,
    name: event.name.trim(),
    is_active: Boolean(event.is_active),
    kind,
    split_count: kind === 'score' ? 0 : Number(event.split_count) || 0,
    attempt_count: kind === 'score' ? Number(event.attempt_count) || 10 : 0,
    effectiveness_threshold: kind === 'score' && threshold >= 1 ? threshold : null,
    target_split_ms: kind !== 'score' && Number(event.split_count) > 1 && targetMs ? targetMs : null,
  };
};

const PerformanceSettings = () => {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [disciplines, setDisciplines] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [maxEvents, setMaxEvents] = useState(DEFAULT_MAX_EVENTS);
  const [dirty, setDirty] = useState(false);
  const [editingEventKey, setEditingEventKey] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const { data, isLoading, error } = useQuery(
    'performance-settings',
    async () => {
      const response = await api.get('/performance/settings');
      return response.data;
    }
  );

  useEffect(() => {
    if (!data) return;
    setEnabled(Boolean(data.enabled));
    setMaxEvents(data.max_events_per_discipline || DEFAULT_MAX_EVENTS);
    setDisciplines(
      (data.disciplines || []).map((item) => ({
        ...item,
        events: (item.events || []).map((event) => ({
          ...event,
          target_split: formatDurationInput(event.target_split_ms),
        })),
      }))
    );
    setDirty(false);
  }, [data]);

  const saveMutation = useMutation(
    async ({ payload, message, afterSuccess }) => {
      const response = await api.put('/performance/settings', payload);
      return { saved: response.data, message, afterSuccess };
    },
    {
      onSuccess: ({ saved, message, afterSuccess }) => {
        queryClient.setQueryData('performance-settings', saved);
        setDirty(false);
        if (afterSuccess) afterSuccess();
        if (message) toast.success(message);
      },
      onError: (err) => {
        toast.error(getApiErrorMessage(err, 'No se pudo guardar la configuración'));
      },
    }
  );

  const deleteMutation = useMutation(
    async ({ eventId, confirm }) => {
      const response = await api.delete(`/performance/events/${eventId}`, {
        params: { confirm },
      });
      return response.data;
    },
    {
      onSuccess: (saved) => {
        queryClient.setQueryData('performance-settings', saved);
        toast.success('Prueba eliminada');
      },
      onError: (err, variables) => {
        const plan = planDeleteFailure(err, variables);
        if (plan.kind === 'confirm') {
          setConfirmDialog({
            title: plan.title,
            message: plan.message,
            type: 'danger',
            confirmText: 'Quitar',
            onConfirm: () => deleteMutation.mutate({
              eventId: variables.eventId,
              eventName: variables.eventName,
              confirm: true,
            }),
          });
          return;
        }
        toast.error(getApiErrorMessage(err, 'No se pudo eliminar la prueba'));
      },
    }
  );

  const isBusy = saveMutation.isLoading || deleteMutation.isLoading;

  const persist = (nextEnabled, nextDisciplines, message, afterSuccess) => {
    setEnabled(nextEnabled);
    setDisciplines(nextDisciplines);
    setDirty(true);
    saveMutation.mutate({
      message,
      afterSuccess,
      payload: {
        enabled: nextEnabled,
        disciplines: nextDisciplines.map((item) => ({
          discipline_id: item.discipline_id,
          events: item.events
            .filter((event) => event.name && event.name.trim())
            .map(toPayloadEvent),
        })),
      },
    });
  };

  const saveChanges = () => {
    const hasEmptyName = disciplines.some((item) =>
      item.events.some((event) => !event.name || !event.name.trim())
    );
    if (hasEmptyName) {
      toast.error('Todas las pruebas deben tener un nombre');
      return;
    }
    persist(enabled, disciplines, 'Configuración guardada');
  };

  const eventKey = (disciplineId, event, index) =>
    `${disciplineId}:${event.id || event.code || index}`;

  const saveEvent = (disciplineId, index) => {
    const discipline = disciplines.find((item) => item.discipline_id === disciplineId);
    const event = discipline?.events[index];
    if (!event?.name || !event.name.trim()) {
      toast.error('La prueba debe tener un nombre');
      return;
    }

    const targetMs = Number(event.target_split_ms) || parseDurationToMs(event.target_split);
    if (event.kind !== 'score' && event.target_split && !targetMs) {
      toast.error('Selecciona un tiempo objetivo válido');
      return;
    }

    persist(
      enabled,
      disciplines,
      'PRUEBA ACTUALIZADA CORRECTAMENTE.',
      () => setEditingEventKey(null)
    );
  };

  const toggleEnabled = (checked) => {
    setEnabled(checked);
    setDirty(true);
  };

  const mapEventPatch = (event, patch) => {
    const updated = { ...event, ...patch };
    if (updated.kind === 'score') {
      updated.split_count = 0;
      updated.attempt_count = Number(updated.attempt_count) || 10;
      updated.subtitle = `${updated.attempt_count} tiros`;
      updated.target_split_ms = null;
      updated.target_split = '';
    } else {
      updated.kind = 'timed';
      updated.attempt_count = 0;
      updated.effectiveness_threshold = null;
      const splits = Number(updated.split_count) || 0;
      updated.subtitle = splits > 1 ? `${splits} parciales` : 'Tiempo total';
      if (splits <= 1) {
        updated.target_split_ms = null;
        updated.target_split = '';
      }
    }
    return updated;
  };

  const withEventPatch = (disciplineId, index, patch) =>
    disciplines.map((item) => {
      if (item.discipline_id !== disciplineId) return item;
      return {
        ...item,
        events: item.events.map((event, eventIndex) =>
          eventIndex === index ? mapEventPatch(event, patch) : event
        ),
      };
    });

  const updateEvent = (disciplineId, index, patch) => {
    setDisciplines(withEventPatch(disciplineId, index, patch));
    setDirty(true);
  };

  const patchEventLocal = (disciplineId, index, patch) => {
    setDisciplines(withEventPatch(disciplineId, index, patch));
    setDirty(true);
  };

  const addEvent = (disciplineId) => {
    if (editingEventKey !== null) {
      toast.info('Guarda la prueba que estás editando antes de agregar otra');
      return;
    }
    const draft = drafts[disciplineId] || emptyDraft();
    const name = draft.name.trim();
    if (!name) {
      toast.error('Escribe el nombre de la prueba');
      return;
    }
    const current = disciplines.find((item) => item.discipline_id === disciplineId);
    if (current && !canAddActiveEvent(current.events, maxEvents)) {
      toast.error(`Máximo ${maxEvents} pruebas por disciplina`);
      return;
    }
    const kind = draft.kind === 'score' ? 'score' : 'timed';
    const splitCount = kind === 'timed' ? Number(draft.split_count) || 0 : 0;
    const attemptCount = kind === 'score' ? Number(draft.attempt_count) || 10 : 0;
    const threshold = Number(draft.effectiveness_threshold);
    const targetMs = parseDurationToMs(draft.target_split);
    const next = disciplines.map((item) => {
      if (item.discipline_id !== disciplineId) return item;
      return {
        ...item,
        events: [
          ...item.events,
          {
            id: null,
            code: '',
            name,
            is_active: true,
            is_preset: false,
            kind,
            split_count: splitCount,
            attempt_count: attemptCount,
            effectiveness_threshold: kind === 'score' && threshold >= 1 ? threshold : null,
            target_split_ms: kind !== 'score' && splitCount > 1 && targetMs ? targetMs : null,
            target_split: draft.target_split || '',
            subtitle: kind === 'score'
              ? `${attemptCount} tiros`
              : splitCount > 1
                ? `${splitCount} parciales`
                : 'Tiempo total',
          },
        ],
      };
    });
    setDrafts((prev) => ({ ...prev, [disciplineId]: emptyDraft() }));
    persist(enabled, next, 'PRUEBA GRABADA CORRECTAMENTE.');
  };

  const removeEvent = (disciplineId, index) => {
    const current = disciplines.find((item) => item.discipline_id === disciplineId);
    const event = current?.events[index];
    if (!event) return;

    if (!event.id) {
      const next = disciplines.map((item) => {
        if (item.discipline_id !== disciplineId) return item;
        return { ...item, events: item.events.filter((_, eventIndex) => eventIndex !== index) };
      });
      setDisciplines(next);
      setDirty(true);
      return;
    }

    const plan = planEventRemoval(event);
    if (plan.kind === 'confirm') {
      setConfirmDialog({
        title: plan.title,
        message: plan.message,
        type: 'danger',
        confirmText: 'Quitar',
        onConfirm: () => deleteMutation.mutate({
          eventId: event.id,
          eventName: event.name,
          confirm: true,
        }),
      });
      return;
    }

    deleteMutation.mutate({
      eventId: event.id,
      eventName: event.name,
      confirm: false,
    });
  };

  const setDraft = (disciplineId, patch) => {
    setDrafts((prev) => ({
      ...prev,
      [disciplineId]: { ...(prev[disciplineId] || emptyDraft()), ...patch },
    }));
  };

  const activeCount = disciplines.reduce(
    (total, item) => total + item.events.filter((event) => event.is_active).length,
    0
  );
  const configuredCount = disciplines.filter((item) =>
    item.events.some((event) => event.is_active)
  ).length;

  if (isLoading) {
    return <div className="loading">Cargando configuración de pruebas...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">No se pudo cargar la configuración de pruebas.</div>
      </div>
    );
  }

  return (
    <div className="page-container perf-page">
      <ConfirmDialog
        isOpen={Boolean(confirmDialog)}
        onClose={() => setConfirmDialog(null)}
        onConfirm={confirmDialog?.onConfirm || (() => {})}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        confirmText={confirmDialog?.confirmText}
      />
      <div className="page-header perf-header">
        <div>
          <h2>Pruebas</h2>
          <p>Configura qué ve el profesor en la app: cronómetro o penales, por disciplina.</p>
        </div>
        <div className="perf-save-wrap">
          {dirty ? <span className="perf-dirty">Hay cambios sin guardar</span> : <span className="perf-clean">Todo guardado</span>}
          <button
            type="button"
            className="btn btn-primary"
            onClick={saveChanges}
            disabled={isBusy || !dirty || editingEventKey !== null}
          >
            {saveMutation.isLoading ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      <div className={`card perf-hero ${enabled ? 'is-on' : ''}`}>
        <label className="perf-switch">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => toggleEnabled(e.target.checked)}
            disabled={isBusy}
          />
          <span>
            <strong>{enabled ? 'Módulo activo' : 'Módulo apagado'}</strong>
            <small>Pulsa Guardar cambios para grabar. Sin esto activo, el icono no aparece en la app.</small>
          </span>
        </label>
        <div className="perf-stats">
          <span className="perf-stat">{configuredCount} disciplinas con pruebas</span>
          <span className="perf-stat">{activeCount} pruebas activas</span>
          <span className="perf-stat">Máx. {maxEvents} por disciplina</span>
        </div>
        <div className="perf-legend">
          <span className="perf-chip timed">Cronómetro</span>
          <span>tiempo total o con parciales</span>
          <span className="perf-chip score">Penales</span>
          <span>goles, fallos y efectividad</span>
        </div>
      </div>

      {disciplines.map((item) => {
        const draft = drafts[item.discipline_id] || emptyDraft();
        const activeEvents = activeEventCount(item.events);
        const atLimit = !canAddActiveEvent(item.events, maxEvents);
        return (
          <div key={item.discipline_id} className="card perf-disc">
            <div className="perf-disc-head">
              <div>
                <h3>{item.discipline_name}</h3>
                <p>
                  {item.has_preset
                    ? 'Trae pruebas sugeridas. Activa solo las que usen.'
                    : 'Sin plantilla: crea un cronómetro o una prueba de penales.'}
                </p>
              </div>
              <div className="perf-disc-tags">
                <span className="perf-tag">{activeEvents} activas</span>
                <span className="perf-tag">{activeEvents}/{maxEvents}</span>
              </div>
            </div>

            {item.events.length === 0 ? (
              <div className="perf-empty">Aún no hay pruebas en esta disciplina.</div>
            ) : (
              <div className="perf-events">
                {item.events.map((event, index) => (
                  <div
                    key={`${event.id || event.code || event.name}-${index}`}
                    className={`perf-event ${event.is_active ? 'is-active' : ''} ${
                      editingEventKey === eventKey(item.discipline_id, event, index)
                        ? 'is-editing'
                        : ''
                    }`}
                  >
                    <div className="perf-event-toggle">
                      <input
                        type="checkbox"
                        checked={Boolean(event.is_active)}
                        aria-label={`Activar ${event.name}`}
                        onChange={() =>
                          updateEvent(item.discipline_id, index, { is_active: !event.is_active })
                        }
                        disabled={
                          isBusy ||
                          editingEventKey !== eventKey(item.discipline_id, event, index)
                        }
                      />
                      <input
                        type="text"
                        className="perf-event-name-input"
                        value={event.name}
                        aria-label="Nombre de la prueba"
                        title="Editar nombre de la prueba"
                        maxLength={120}
                        disabled={
                          isBusy ||
                          editingEventKey !== eventKey(item.discipline_id, event, index)
                        }
                        onChange={(e) =>
                          patchEventLocal(item.discipline_id, index, { name: e.target.value })
                        }
                      />
                    </div>
                    <div className="perf-event-meta">
                      <select
                        className="perf-select"
                        value={event.kind === 'score' ? 'score' : 'timed'}
                        disabled={
                          isBusy ||
                          editingEventKey !== eventKey(item.discipline_id, event, index)
                        }
                        onChange={(e) =>
                          updateEvent(item.discipline_id, index, { kind: e.target.value })
                        }
                      >
                        <option value="timed">Cronómetro</option>
                        <option value="score">Penales</option>
                      </select>
                      {event.kind === 'score' ? (
                        <label className="perf-mini">
                          Tiros
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={event.attempt_count || 10}
                            disabled={
                              isBusy ||
                              editingEventKey !== eventKey(item.discipline_id, event, index)
                            }
                            onChange={(e) =>
                              patchEventLocal(item.discipline_id, index, {
                                attempt_count: e.target.value,
                              })
                            }
                          />
                        </label>
                      ) : (
                        <label className="perf-mini">
                          Parciales
                          <input
                            type="number"
                            min="0"
                            max="20"
                            value={event.split_count || 0}
                            disabled={
                              isBusy ||
                              editingEventKey !== eventKey(item.discipline_id, event, index)
                            }
                            onChange={(e) =>
                              patchEventLocal(item.discipline_id, index, {
                                split_count: e.target.value,
                              })
                            }
                          />
                        </label>
                      )}
                      {event.kind === 'score' ? (
                        <label className="perf-mini perf-mini-wide">
                          Umbral de efectividad (%)
                          <input
                            type="number"
                            min="1"
                            max="100"
                            placeholder="70"
                            value={event.effectiveness_threshold || ''}
                            disabled={
                              isBusy ||
                              editingEventKey !== eventKey(item.discipline_id, event, index)
                            }
                            onChange={(e) =>
                              patchEventLocal(item.discipline_id, index, {
                                effectiveness_threshold: e.target.value,
                              })
                            }
                          />
                        </label>
                      ) : Number(event.split_count) > 1 ? (
                        <div className="perf-mini perf-mini-wide">
                          <span>Tiempo objetivo por parcial (HH:MM:SS)</span>
                          <input
                            type="time"
                            className="perf-time-input"
                            step="1"
                            value={event.target_split || formatDurationInput(event.target_split_ms)}
                            aria-label="Tiempo objetivo por parcial en horas, minutos y segundos"
                            title="Usa las flechas izquierda y derecha para cambiar entre horas, minutos y segundos"
                            disabled={
                              isBusy ||
                              editingEventKey !== eventKey(item.discipline_id, event, index)
                            }
                            onChange={(e) =>
                              patchEventLocal(item.discipline_id, index, {
                                target_split: e.target.value,
                                target_split_ms: parseDurationToMs(e.target.value),
                              })
                            }
                          />
                        </div>
                      ) : null}
                      <span className="perf-event-sub">{event.subtitle || 'Tiempo total'}</span>
                      <div className="perf-event-actions">
                        <button
                          type="button"
                          className={
                            editingEventKey === eventKey(item.discipline_id, event, index)
                              ? 'perf-save-event'
                              : 'perf-edit-event'
                          }
                          disabled={
                            isBusy ||
                            (editingEventKey !== null &&
                              editingEventKey !== eventKey(item.discipline_id, event, index))
                          }
                          onClick={() => {
                            if (editingEventKey === eventKey(item.discipline_id, event, index)) {
                              saveEvent(item.discipline_id, index);
                            } else {
                              setEditingEventKey(eventKey(item.discipline_id, event, index));
                            }
                          }}
                        >
                          {editingEventKey === eventKey(item.discipline_id, event, index)
                            ? 'Guardar prueba'
                            : 'Editar'}
                        </button>
                        <button
                          type="button"
                          className="perf-delete"
                          title="Eliminar prueba"
                          disabled={isBusy || editingEventKey !== null}
                          onClick={() => removeEvent(item.discipline_id, index)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="perf-add">
              <div className="perf-add-types">
                <button
                  type="button"
                  className={`perf-type-btn ${draft.kind !== 'score' ? 'is-selected' : ''}`}
                  onClick={() => setDraft(item.discipline_id, { kind: 'timed' })}
                >
                  Cronómetro
                </button>
                <button
                  type="button"
                  className={`perf-type-btn score ${draft.kind === 'score' ? 'is-selected' : ''}`}
                  onClick={() => setDraft(item.discipline_id, { kind: 'score' })}
                >
                  Penales
                </button>
              </div>
              <div className="perf-add-row">
                <input
                  type="text"
                  className="perf-add-input"
                  placeholder={
                    atLimit
                      ? `Máximo ${maxEvents} pruebas`
                      : draft.kind === 'score'
                        ? 'Ej. Penales, Tiros libres'
                        : 'Ej. 50 m libre, 4 × 100 metros'
                  }
                  value={draft.name}
                  disabled={atLimit || isBusy}
                  onChange={(e) => setDraft(item.discipline_id, { name: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addEvent(item.discipline_id);
                    }
                  }}
                />
                {draft.kind === 'score' ? (
                  <label className="perf-mini">
                    Tiros
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={draft.attempt_count}
                      disabled={atLimit || isBusy}
                      onChange={(e) =>
                        setDraft(item.discipline_id, { attempt_count: e.target.value })
                      }
                    />
                  </label>
                ) : (
                  <label className="perf-mini">
                    Parciales
                    <input
                      type="number"
                      min="0"
                      max="20"
                      value={draft.split_count}
                      disabled={atLimit || isBusy}
                      onChange={(e) =>
                        setDraft(item.discipline_id, { split_count: e.target.value })
                      }
                    />
                  </label>
                )}
                {draft.kind === 'score' ? (
                  <label className="perf-mini perf-mini-wide">
                    Umbral de efectividad (%)
                    <input
                      type="number"
                      min="1"
                      max="100"
                      placeholder="70"
                      value={draft.effectiveness_threshold}
                      disabled={atLimit || isBusy}
                      onChange={(e) =>
                        setDraft(item.discipline_id, { effectiveness_threshold: e.target.value })
                      }
                    />
                  </label>
                ) : Number(draft.split_count) > 1 ? (
                  <label className="perf-mini perf-mini-wide">
                    Tiempo objetivo por parcial (HH:MM:SS)
                    <input
                      type="time"
                      className="perf-time-input"
                      step="1"
                      value={draft.target_split}
                      aria-label="Tiempo objetivo por parcial en horas, minutos y segundos"
                      title="Usa las flechas izquierda y derecha para cambiar entre horas, minutos y segundos"
                      disabled={atLimit || isBusy}
                      onChange={(e) =>
                        setDraft(item.discipline_id, { target_split: e.target.value })
                      }
                    />
                  </label>
                ) : null}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => addEvent(item.discipline_id)}
                  disabled={atLimit || isBusy || editingEventKey !== null}
                >
                  Agregar
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PerformanceSettings;
